import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Calendar, MessageCircle, BarChart3, LogOut, Check, X, Trash2, Clock, ChevronDown, BookOpen, Plus, Pencil, Image } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { HomeCareLogoIcon } from "@/components/HomeCareLogoIcon";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const axiosAuth = axios.create({ withCredentials: true });

const errDetail = (err, fallback) => err.response?.data?.detail || fallback;

// --- Small extracted components ---

const StatusBadge = ({ status }) => {
  const colors = {
    pending: "bg-yellow-100 text-yellow-700",
    confirmed: "bg-green-100 text-green-700",
    completed: "bg-blue-100 text-blue-700",
    cancelled: "bg-red-100 text-red-700",
  };
  return (
    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${colors[status] || colors.pending}`}>
      {status}
    </span>
  );
};

const PatientInfoPanel = ({ appointment }) => {
  const a = appointment;
  return (
    <div className="mt-3 p-4 bg-[#F3F0E7] rounded-xl text-sm space-y-1">
      {a.patient_name && <p><strong>Patient:</strong> {a.patient_name} {a.patient_age && `(${a.patient_age} yrs)`}</p>}
      {a.patient_type && <p><strong>Type:</strong> {a.patient_type}</p>}
      {a.patient_condition && <p><strong>Condition:</strong> {a.patient_condition}</p>}
      {a.medical_history && <p><strong>History:</strong> {a.medical_history}</p>}
      {a.special_needs?.length > 0 && <p><strong>Needs:</strong> {a.special_needs.join(", ")}</p>}
      {a.notes && <p><strong>Notes:</strong> {a.notes}</p>}
    </div>
  );
};

const AppointmentActions = ({ appointment, onUpdateStatus, onDelete }) => {
  const a = appointment;
  return (
    <div className="flex items-center gap-2 ml-4">
      {a.status === "pending" && (
        <Button size="sm" onClick={() => onUpdateStatus(a.id, "confirmed")} className="bg-green-500 hover:bg-green-600 text-white rounded-full">
          <Check className="w-4 h-4" />
        </Button>
      )}
      {a.status === "confirmed" && (
        <Button size="sm" onClick={() => onUpdateStatus(a.id, "completed")} className="bg-blue-500 hover:bg-blue-600 text-white rounded-full">
          <Check className="w-4 h-4" />
        </Button>
      )}
      <Button size="sm" variant="outline" onClick={() => onUpdateStatus(a.id, "cancelled")} className="rounded-full border-red-200 text-red-500 hover:bg-red-50">
        <X className="w-4 h-4" />
      </Button>
      <Button size="sm" variant="outline" onClick={() => onDelete(a.id)} className="rounded-full border-gray-200 text-gray-400 hover:text-red-500 hover:bg-red-50">
        <Trash2 className="w-4 h-4" />
      </Button>
    </div>
  );
};

const AppointmentCard = ({ appointment, expandedAppt, setExpandedAppt, onUpdateStatus, onDelete }) => {
  const a = appointment;
  const isExpanded = expandedAppt === a.id;
  return (
    <Card data-testid={`appt-card-${a.id}`} className="border-0 shadow-lg">
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <h3 className="font-semibold text-[#1A2332]">{a.client_name}</h3>
              <StatusBadge status={a.status} />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm text-[#4A5568]">
              <span>{a.email}</span>
              <span>{a.phone}</span>
              <span>{a.preferred_date}</span>
              <span>{a.preferred_time}</span>
            </div>
            {a.patient_name && (
              <button onClick={() => setExpandedAppt(isExpanded ? null : a.id)} className="mt-2 text-sm text-[#C89D4B] flex items-center gap-1 hover:underline">
                Patient Info <ChevronDown className={`w-3 h-3 transition-transform ${isExpanded ? "rotate-180" : ""}`} />
              </button>
            )}
            {isExpanded && <PatientInfoPanel appointment={a} />}
          </div>
          <AppointmentActions appointment={a} onUpdateStatus={onUpdateStatus} onDelete={onDelete} />
        </div>
      </CardContent>
    </Card>
  );
};

// --- Custom hooks ---

const useAuth = () => {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    axiosAuth.get(`${API}/auth/me`)
      .then(r => setUser(r.data))
      .catch(() => setUser(null))
      .finally(() => setChecking(false));
  }, []);

  const login = useCallback(async (email, password) => {
    const { data } = await axiosAuth.post(`${API}/auth/login`, { email, password });
    setUser(data);
    return data;
  }, []);

  const logout = useCallback(() => {
    axiosAuth.post(`${API}/auth/logout`).catch(() => {});
    setUser(null);
  }, []);

  return { user, checking, login, logout };
};

const useAdminData = (user) => {
  const [data, setData] = useState({ stats: {}, appointments: [], contacts: [], blogPosts: [] });
  const [dataError, setDataError] = useState("");

  const loadData = useCallback(async () => {
    try {
      setDataError("");
      const [s, a, c, b] = await Promise.all([
        axiosAuth.get(`${API}/admin/stats`),
        axiosAuth.get(`${API}/admin/appointments`),
        axiosAuth.get(`${API}/admin/contacts`),
        axiosAuth.get(`${API}/blog`),
      ]);
      setData({ stats: s.data, appointments: a.data, contacts: c.data, blogPosts: b.data });
    } catch (err) {
      setDataError(errDetail(err, "Failed to load data"));
    }
  }, []);

  useEffect(() => { if (user) loadData(); }, [user, loadData]);

  const runAction = useCallback(async (action, failMsg) => {
    try {
      await action();
      loadData();
    } catch (err) {
      setDataError(errDetail(err, failMsg));
    }
  }, [loadData]);

  const updateStatus = useCallback((id, status) => runAction(
    () => axiosAuth.put(`${API}/admin/appointments/${id}/status`, { status }),
    "Failed to update status"
  ), [runAction]);

  const deleteAppt = useCallback((id) => {
    if (!window.confirm("Delete this appointment?")) return;
    runAction(() => axiosAuth.delete(`${API}/admin/appointments/${id}`), "Failed to delete appointment");
  }, [runAction]);

  const deleteContact = useCallback((id) => {
    if (!window.confirm("Delete this message?")) return;
    runAction(() => axiosAuth.delete(`${API}/admin/contacts/${id}`), "Failed to delete message");
  }, [runAction]);

  const createBlogPost = useCallback(async (postData) => {
    await axiosAuth.post(`${API}/admin/blog`, postData);
    loadData();
  }, [loadData]);

  const updateBlogPost = useCallback(async (id, postData) => {
    await axiosAuth.put(`${API}/admin/blog/${id}`, postData);
    loadData();
  }, [loadData]);

  const deleteBlogPost = useCallback((id) => {
    if (!window.confirm("Delete this blog post?")) return;
    runAction(() => axiosAuth.delete(`${API}/admin/blog/${id}`), "Failed to delete blog post");
  }, [runAction]);

  return { ...data, dataError, updateStatus, deleteAppt, deleteContact, createBlogPost, updateBlogPost, deleteBlogPost };
};

// --- Login component ---

const AdminLogin = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await onLogin(email, password);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F3F0E7] via-white to-[#F8F5EE] flex items-center justify-center p-6">
      <Card className="w-full max-w-md border-0 shadow-2xl">
        <CardHeader className="text-center pb-2">
          <div className="flex justify-center mb-4">
            <HomeCareLogoIcon className="h-16 w-auto" />
          </div>
          <CardTitle className="font-['Playfair_Display'] text-2xl text-[#1A2332]">Admin Dashboard</CardTitle>
          <p className="text-[#718096] text-sm">Caceres Essential Care</p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && <div data-testid="login-error" className="bg-red-50 text-red-600 p-3 rounded-lg text-sm">{error}</div>}
            <div className="space-y-2">
              <Label>Email</Label>
              <Input data-testid="admin-email" type="email" value={email} onChange={e => setEmail(e.target.value)} required className="border-[#003A4B]/20" />
            </div>
            <div className="space-y-2">
              <Label>Password</Label>
              <Input data-testid="admin-password" type="password" value={password} onChange={e => setPassword(e.target.value)} required className="border-[#003A4B]/20" />
            </div>
            <Button type="submit" data-testid="admin-login-btn" disabled={loading} className="w-full bg-[#003A4B] hover:bg-[#002A38] text-white rounded-full py-5">
              {loading ? "..." : "Sign In"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

// --- Blog Editor Component ---

const BLOG_CATEGORIES = ["Patient Care", "Specialized Care", "Safety", "Nutrition", "Mental Health", "General Tips"];

const BlogMetaFields = ({ form, setForm }) => (
  <div className="grid grid-cols-2 gap-4">
    <div className="space-y-2">
      <Label>Category</Label>
      <select data-testid="blog-category-select" value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))} className="w-full rounded-md border border-[#003A4B]/20 px-3 py-2 text-sm bg-white">
        {BLOG_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
      </select>
    </div>
    <div className="space-y-2">
      <Label>Read Time</Label>
      <Input data-testid="blog-readtime-input" value={form.read_time} onChange={e => setForm(f => ({ ...f, read_time: e.target.value }))} placeholder="5 min" className="border-[#003A4B]/20" />
    </div>
  </div>
);

const BlogImageField = ({ form, setForm }) => (
  <div className="space-y-2">
    <Label className="flex items-center gap-1"><Image className="w-4 h-4" /> Image URL</Label>
    <Input data-testid="blog-image-input" value={form.image_url} onChange={e => setForm(f => ({ ...f, image_url: e.target.value }))} placeholder="https://images.unsplash.com/..." className="border-[#003A4B]/20" />
    {form.image_url && (
      <div className="h-32 rounded-lg overflow-hidden mt-2">
        <img src={form.image_url} alt="Preview" className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
      </div>
    )}
  </div>
);

const BlogContentFields = ({ form, setForm }) => (
  <>
    <div className="space-y-2">
      <Label>Excerpt (Short description)</Label>
      <Input data-testid="blog-excerpt-input" value={form.excerpt} onChange={e => setForm(f => ({ ...f, excerpt: e.target.value }))} required placeholder="Brief description..." className="border-[#003A4B]/20" />
    </div>
    <div className="space-y-2">
      <Label>Content (Use **bold** and separate paragraphs with blank lines)</Label>
      <textarea data-testid="blog-content-input" value={form.content} onChange={e => setForm(f => ({ ...f, content: e.target.value }))} required rows={10} className="w-full rounded-md border border-[#003A4B]/20 px-3 py-2 text-sm resize-y min-h-[200px]" placeholder="Write your article content here..." />
    </div>
  </>
);

const BlogEditor = ({ post, onSave, onCancel }) => {
  const [form, setForm] = useState({
    title: post?.title || "",
    excerpt: post?.excerpt || "",
    content: post?.content || "",
    category: post?.category || "Patient Care",
    image_url: post?.image_url || "",
    read_time: post?.read_time || "5 min",
  });
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave(form);
    } catch (err) {
      setSaveError(errDetail(err, "Failed to save post"));
    } finally {
      setSaving(false);
    }
  };

  const getButtonLabel = () => {
    if (saving) return "Saving...";
    return post ? "Update Post" : "Publish Post";
  };

  return (
    <Card className="border-0 shadow-lg">
      <CardHeader>
        <CardTitle className="text-[#1A2332]">{post ? "Edit Post" : "New Blog Post"}</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          {saveError && <div data-testid="blog-save-error" className="bg-red-50 text-red-600 p-3 rounded-lg text-sm">{saveError}</div>}
          <div className="space-y-2">
            <Label>Title</Label>
            <Input data-testid="blog-title-input" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} required className="border-[#003A4B]/20" placeholder="Article title..." />
          </div>
          <BlogMetaFields form={form} setForm={setForm} />
          <BlogImageField form={form} setForm={setForm} />
          <BlogContentFields form={form} setForm={setForm} />
          <div className="flex gap-3 pt-2">
            <Button type="submit" data-testid="blog-save-btn" disabled={saving} className="bg-[#003A4B] hover:bg-[#002A38] text-white rounded-full px-8">
              {getButtonLabel()}
            </Button>
            <Button type="button" variant="outline" onClick={onCancel} className="rounded-full border-[#003A4B]/20">
              Cancel
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

// --- Tab Content Components ---

const OverviewTab = ({ stats, appointments }) => {
  const statCards = [
    { id: "total", label: "Total Appointments", value: stats.total_appointments || 0, icon: Calendar, color: "from-[#003A4B] to-[#1B5E6B]" },
    { id: "pending", label: "Pending", value: stats.pending || 0, icon: Clock, color: "from-[#C89D4B] to-[#D4B06A]" },
    { id: "confirmed", label: "Confirmed", value: stats.confirmed || 0, icon: Check, color: "from-[#22C55E] to-[#16A34A]" },
    { id: "messages", label: "Messages", value: stats.total_contacts || 0, icon: MessageCircle, color: "from-[#003A4B] to-[#1B5E6B]" },
  ];

  return (
    <div className="space-y-8">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {statCards.map((s) => (
          <Card key={s.id} className="border-0 shadow-lg">
            <CardContent className="p-6">
              <div className={`w-12 h-12 bg-gradient-to-br ${s.color} rounded-xl flex items-center justify-center mb-3`}>
                <s.icon className="w-6 h-6 text-white" />
              </div>
              <p className="text-3xl font-bold text-[#1A2332]">{s.value}</p>
              <p className="text-sm text-[#718096]">{s.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>
      <Card className="border-0 shadow-lg">
        <CardHeader><CardTitle className="text-[#1A2332]">Recent Appointments</CardTitle></CardHeader>
        <CardContent>
          {appointments.slice(0, 5).map((a) => (
            <div key={a.id} className="flex items-center justify-between py-3 border-b border-gray-100 last:border-0">
              <div>
                <p className="font-medium text-[#1A2332]">{a.client_name}</p>
                <p className="text-sm text-[#718096]">{a.preferred_date} - {a.preferred_time}</p>
              </div>
              <StatusBadge status={a.status} />
            </div>
          ))}
          {appointments.length === 0 && <p className="text-[#718096] text-center py-8">No appointments yet</p>}
        </CardContent>
      </Card>
    </div>
  );
};

const AppointmentsTab = ({ appointments, expandedAppt, setExpandedAppt, updateStatus, deleteAppt }) => (
  <div className="space-y-4">
    {appointments.map((a) => (
      <AppointmentCard
        key={a.id}
        appointment={a}
        expandedAppt={expandedAppt}
        setExpandedAppt={setExpandedAppt}
        onUpdateStatus={updateStatus}
        onDelete={deleteAppt}
      />
    ))}
    {appointments.length === 0 && <Card className="border-0 shadow-lg"><CardContent className="p-12 text-center text-[#718096]">No appointments yet</CardContent></Card>}
  </div>
);

const ContactsTab = ({ contacts, deleteContact }) => (
  <div className="space-y-4">
    {contacts.map((c) => (
      <Card key={c.id} data-testid={`contact-card-${c.id}`} className="border-0 shadow-lg">
        <CardContent className="p-6">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="font-semibold text-[#1A2332] mb-1">{c.name}</h3>
              <p className="text-sm text-[#718096] mb-2">{c.email} {c.phone && `| ${c.phone}`}</p>
              <p className="text-[#4A5568]">{c.message}</p>
              <p className="text-xs text-[#718096] mt-2">{c.created_at}</p>
            </div>
            <Button size="sm" variant="outline" onClick={() => deleteContact(c.id)} className="rounded-full border-gray-200 text-gray-400 hover:text-red-500">
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </CardContent>
      </Card>
    ))}
    {contacts.length === 0 && <Card className="border-0 shadow-lg"><CardContent className="p-12 text-center text-[#718096]">No messages yet</CardContent></Card>}
  </div>
);

const BlogAdminCard = ({ post, onEdit, onDelete }) => (
  <Card data-testid={`blog-admin-card-${post.id}`} className="border-0 shadow-lg">
    <CardContent className="p-6">
      <div className="flex items-start gap-4">
        {post.image_url && (
          <div className="w-24 h-24 rounded-lg overflow-hidden flex-shrink-0">
            <img src={post.image_url} alt={post.title} className="w-full h-full object-cover" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xs font-semibold text-[#003A4B] bg-[#003A4B]/10 px-2 py-0.5 rounded-full">{post.category}</span>
            <span className="text-xs text-[#718096]">{post.read_time}</span>
          </div>
          <h3 className="font-semibold text-[#1A2332] mb-1 truncate">{post.title}</h3>
          <p className="text-sm text-[#4A5568] line-clamp-2">{post.excerpt}</p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <Button size="sm" variant="outline" data-testid={`blog-edit-${post.id}`} onClick={onEdit} className="rounded-full border-[#003A4B]/20 text-[#003A4B]">
            <Pencil className="w-4 h-4" />
          </Button>
          <Button size="sm" variant="outline" data-testid={`blog-delete-${post.id}`} onClick={onDelete} className="rounded-full border-gray-200 text-gray-400 hover:text-red-500 hover:bg-red-50">
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </CardContent>
  </Card>
);

const BlogEmptyState = () => (
  <Card className="border-0 shadow-lg">
    <CardContent className="p-12 text-center text-[#718096]">
      <BookOpen className="w-12 h-12 mx-auto mb-3 text-[#003A4B]/20" />
      <p>No blog posts yet. Create your first post!</p>
    </CardContent>
  </Card>
);

const BlogListView = ({ blogPosts, setBlogEditing, deleteBlogPost }) => (
  <>
    <div className="flex justify-between items-center mb-2">
      <h2 className="text-xl font-bold text-[#1A2332]">Blog Posts ({blogPosts.length})</h2>
      <Button data-testid="blog-new-btn" onClick={() => setBlogEditing("new")} className="bg-[#003A4B] hover:bg-[#002A38] text-white rounded-full">
        <Plus className="w-4 h-4 mr-1" /> New Post
      </Button>
    </div>
    {blogPosts.map((post) => (
      <BlogAdminCard key={post.id} post={post} onEdit={() => setBlogEditing(post)} onDelete={() => deleteBlogPost(post.id)} />
    ))}
    {blogPosts.length === 0 && <BlogEmptyState />}
  </>
);

const BlogTab = ({ blogPosts, blogEditing, setBlogEditing, createBlogPost, updateBlogPost, deleteBlogPost }) => {
  const handleSave = async (data) => {
    if (blogEditing === "new") {
      await createBlogPost(data);
    } else {
      await updateBlogPost(blogEditing.id, data);
    }
    setBlogEditing(null);
  };

  return (
    <div className="space-y-4">
      {blogEditing ? (
        <BlogEditor post={blogEditing === "new" ? null : blogEditing} onSave={handleSave} onCancel={() => setBlogEditing(null)} />
      ) : (
        <BlogListView blogPosts={blogPosts} setBlogEditing={setBlogEditing} deleteBlogPost={deleteBlogPost} />
      )}
    </div>
  );
};

// --- Main Dashboard ---

const DASHBOARD_TABS = [
  { id: "overview", label: "Overview", icon: BarChart3 },
  { id: "appointments", label: "Appointments", icon: Calendar },
  { id: "contacts", label: "Messages", icon: MessageCircle },
  { id: "blog", label: "Blog", icon: BookOpen },
];

const DashboardHeader = ({ user, logout }) => (
  <header className="bg-white shadow-sm border-b border-[#003A4B]/10 px-6 py-3">
    <div className="max-w-7xl mx-auto flex items-center justify-between">
      <div className="flex items-center gap-3">
        <HomeCareLogoIcon className="h-10 w-auto" />
        <div>
          <h1 className="font-['Playfair_Display'] font-bold text-[#1A2332]">Admin Dashboard</h1>
          <p className="text-xs text-[#718096]">Caceres Essential Care</p>
        </div>
      </div>
      <div className="flex items-center gap-4">
        <span className="text-sm text-[#4A5568]">{user.email}</span>
        <Button variant="outline" size="sm" onClick={logout} data-testid="admin-logout" className="rounded-full border-[#003A4B]/20">
          <LogOut className="w-4 h-4 mr-1" /> Logout
        </Button>
      </div>
    </div>
  </header>
);

const TabsBar = ({ activeTab, setActiveTab }) => (
  <div className="flex gap-2 mb-8">
    {DASHBOARD_TABS.map(tab => (
      <button
        key={tab.id}
        data-testid={`tab-${tab.id}`}
        onClick={() => setActiveTab(tab.id)}
        className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition-all ${
          activeTab === tab.id ? "bg-[#003A4B] text-white shadow-lg" : "bg-white text-[#4A5568] hover:bg-[#003A4B]/5"
        }`}
      >
        <tab.icon className="w-4 h-4" /> {tab.label}
      </button>
    ))}
  </div>
);

const AdminDashboard = () => {
  const { user, checking, login, logout } = useAuth();
  const adminData = useAdminData(user);
  const [activeTab, setActiveTab] = useState("overview");
  const [expandedAppt, setExpandedAppt] = useState(null);
  const [blogEditing, setBlogEditing] = useState(null);

  if (checking) return <div className="min-h-screen flex items-center justify-center bg-[#F3F0E7]"><div className="animate-spin w-8 h-8 border-4 border-[#003A4B] border-t-transparent rounded-full"></div></div>;
  if (!user) return <AdminLogin onLogin={login} />;

  const { stats, appointments, contacts, blogPosts, dataError, updateStatus, deleteAppt, deleteContact, createBlogPost, updateBlogPost, deleteBlogPost } = adminData;

  return (
    <div className="min-h-screen bg-[#F3F0E7]">
      <DashboardHeader user={user} logout={logout} />
      <div className="max-w-7xl mx-auto px-6 py-8">
        <TabsBar activeTab={activeTab} setActiveTab={setActiveTab} />
        {dataError && <div data-testid="admin-error" className="bg-red-50 text-red-600 p-4 rounded-xl text-sm mb-4">{dataError}</div>}
        {activeTab === "overview" && <OverviewTab stats={stats} appointments={appointments} />}
        {activeTab === "appointments" && <AppointmentsTab appointments={appointments} expandedAppt={expandedAppt} setExpandedAppt={setExpandedAppt} updateStatus={updateStatus} deleteAppt={deleteAppt} />}
        {activeTab === "contacts" && <ContactsTab contacts={contacts} deleteContact={deleteContact} />}
        {activeTab === "blog" && <BlogTab blogPosts={blogPosts} blogEditing={blogEditing} setBlogEditing={setBlogEditing} createBlogPost={createBlogPost} updateBlogPost={updateBlogPost} deleteBlogPost={deleteBlogPost} />}
      </div>
    </div>
  );
};

export default AdminDashboard;
